import React, { createContext, useContext, useReducer, useCallback, useMemo, ReactNode } from 'react';
import { Alert, Call } from '@ixigo/icons';
import BottomToast from '../components/Toast/BottomToast';

// Toast state interface
interface ToastState {
    visible: boolean;
    message: string;
    icon: string | null;
    pendingMessage: { message: string; icon: string } | null;
}

// Toast actions
type ToastAction =
    | { type: 'SET_PENDING_TOAST'; payload: { message: string; icon: string } }
    | { type: 'SHOW_TOAST' }
    | { type: 'HIDE_TOAST' };

// Context interface
interface ToastContextType {
    showToast: (message: string, icon: string) => void;
    hideToast: () => void;
    state: ToastState;
}

// Initial state
const initialState: ToastState = {
    visible: false,
    message: '',
    icon: null,
    pendingMessage: null,
};

// Reducer
const toastReducer = (state: ToastState, action: ToastAction): ToastState => {
    switch (action.type) {
        case 'SET_PENDING_TOAST':
            return { ...state, pendingMessage: action.payload };
        case 'SHOW_TOAST':
            return {
                ...state,
                visible: true,
                message: state.pendingMessage?.message || '',
                icon: state.pendingMessage?.icon || null,
                pendingMessage: null,
            };
        case 'HIDE_TOAST':
            return { ...state, visible: false };
        default:
            return state;
    }
};

// Create context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Toast icon component
const ToastIcon: React.FC<{ icon: string | null }> = ({ icon }) => {
    if (icon === 'alert') return <Alert className='text-white' width={26} height={26} />;
    if (icon === 'call') return <Call className='text-white' width={26} height={26} />;
    return null;
};

// Provider component
interface ToastProviderProps {
    children: ReactNode;
}

/**
 * ToastProvider - Provides global toast functionality
 *
 * Wrap your app with this provider to enable toast notifications anywhere in the app.
 *
 * @example
 * ```tsx
 * import { ToastProvider } from './contexts/ToastContext';
 *
 * function App() {
 *   return (
 *     <ToastProvider>
 *       <YourApp />
 *     </ToastProvider>
 *   );
 * }
 * ```
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(toastReducer, initialState);

    const showToast = useCallback((message: string, icon: string) => {
        dispatch({ type: 'SET_PENDING_TOAST', payload: { message, icon } });
        // Show toast immediately
        dispatch({ type: 'SHOW_TOAST' });
    }, []);

    const hideToast = useCallback(() => {
        dispatch({ type: 'HIDE_TOAST' });
    }, []);

    const contextValue = useMemo(
        () => ({
            showToast,
            hideToast,
            state,
        }),
        [showToast, hideToast, state],
    );

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <BottomToast
                heading={state.message}
                visible={state.visible}
                onBackdropClick={hideToast}
                icon={<ToastIcon icon={state.icon} />}
            />
        </ToastContext.Provider>
    );
};

/**
 * useToast - Hook to access toast functionality
 *
 * Use this hook in any component to show toast notifications.
 *
 * @example
 * ```tsx
 * import { useToast } from './contexts/ToastContext';
 *
 * function MyComponent() {
 *   const { showToast } = useToast();
 *
 *   const handleSuccess = () => {
 *     showToast('Operation completed successfully!', 'alert');
 *   };
 *
 *   const handleCall = () => {
 *     showToast('Calling support...', 'call');
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleSuccess}>Show Success</button>
 *       <button onClick={handleCall}>Call Support</button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns {ToastContextType} Object containing showToast, hideToast, and state
 * @throws {Error} When used outside of ToastProvider
 */
export const useToast = (): ToastContextType => {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export default ToastContext;
